import { Collection, ContentResponse, ProfileUserResponse, PublicUserResponse, UserResponse } from "@authory-packages/core";
import { EmbedMode } from "../../utils/embeds";

export enum EmbedTarget {
    CONTENT = "content",
    COLLECTION = "collection",
    PORTFOLIO = "portfolio"
}

export const getEmbedTarget = (
    target: EmbedTarget,
    author: UserResponse | ProfileUserResponse | PublicUserResponse,
    content?: ContentResponse,
    collection?: Collection
) => {
    switch (target) {
        case EmbedTarget.CONTENT:
            return `${process.env.NEXT_PUBLIC_FRONTEND_URL}/${author.slug}/${content?.slug}/embed`;
        case EmbedTarget.COLLECTION:
            return `${process.env.NEXT_PUBLIC_FRONTEND_URL}/${author.slug}/collection/${collection?.slug}/embed`;
        case EmbedTarget.PORTFOLIO:
            return `${process.env.NEXT_PUBLIC_FRONTEND_URL}/${author.slug}/embed`;
        default:
            return "";
    }
};

export const modeOptions = [
    { value: EmbedMode.normal, text: "Normal" },
    { value: EmbedMode.compact, text: "Compact" }
];

const BASE_HEIGHT = 40;

const EMBED_CONTENT_BLOCKS = {
    HEADER: 42,
    METADATA: 32,
    POWEREDBY: 18,
    [EmbedMode.normal]: {
        IMAGE: 198,
        TITLE: 38,
        DESCRIPTION: 90
    },
    [EmbedMode.compact]: {
        CONTENT_PREVIEW_WITH_IMAGE: 166,
        CONTENT_PREVIEW_WITHOUT_IMAGE: 128
    }
};

export const computeEmbedContentHeight = (showHeader: boolean, embedMode: EmbedMode, hasImage: boolean) => {
    let computedHeight = BASE_HEIGHT;

    if (showHeader) computedHeight += EMBED_CONTENT_BLOCKS.HEADER;

    if (embedMode === EmbedMode.normal) {
        if (hasImage) computedHeight += EMBED_CONTENT_BLOCKS[EmbedMode.normal].IMAGE;
        computedHeight += EMBED_CONTENT_BLOCKS[EmbedMode.normal].TITLE;
        computedHeight += EMBED_CONTENT_BLOCKS[EmbedMode.normal].DESCRIPTION;
    }

    if (embedMode === EmbedMode.compact) {
        computedHeight += hasImage
            ? EMBED_CONTENT_BLOCKS[EmbedMode.compact].CONTENT_PREVIEW_WITH_IMAGE
            : EMBED_CONTENT_BLOCKS[EmbedMode.compact].CONTENT_PREVIEW_WITHOUT_IMAGE;
    }

    computedHeight += EMBED_CONTENT_BLOCKS.METADATA;
    computedHeight += EMBED_CONTENT_BLOCKS.POWEREDBY;

    return computedHeight;
};

const EMBED_COLLECTION_BLOCKS = {
    META: 24,
    TITLE_WITH_DESC: 68,
    TITLE_WITHOUT_DESC: 46,
    SEPARATOR: 17,
    VIEW_ON_AUTHORY: 18
};

const getListHeight = (numItems: number) => (numItems === 0 ? 0 : Math.max(0, numItems - 1) * 74 + Math.max(0, numItems - (numItems - 1)) * 78);

export const computeEmbedCollectiontHeight = (showHeader: boolean, hasDescription: boolean, numItems: number) => {
    let computedHeight = BASE_HEIGHT;

    if (showHeader) {
        computedHeight += EMBED_CONTENT_BLOCKS.HEADER;
        computedHeight += EMBED_COLLECTION_BLOCKS.META;

        if (hasDescription) {
            computedHeight += EMBED_COLLECTION_BLOCKS.TITLE_WITH_DESC;
        } else {
            computedHeight += EMBED_COLLECTION_BLOCKS.TITLE_WITHOUT_DESC;
        }

        computedHeight += EMBED_COLLECTION_BLOCKS.SEPARATOR;
    }

    computedHeight += getListHeight(numItems);

    computedHeight += EMBED_COLLECTION_BLOCKS.VIEW_ON_AUTHORY;

    return computedHeight;
};

const EMBED_PROFILE_BLOCKS = {
    [EmbedMode.normal]: {
        PROFILE_PHOTO: 60,
        TITLE: 40,
        BIO: 30,
        SOCIALS_CONTACT: 36
    },
    [EmbedMode.compact]: {
        WITH_SOCIALS: 106,
        WITH_IMAGE: 80,
        WITHOUT_SOCIALS_AND_IMAGE: 70
    },
    SEPARATOR: 37,
    BACKGROUND_NO_ITEMS_FOOTER: 28
};

export const computeEmbedProfileHeight = (
    embedMode: EmbedMode,
    hasSocialsOrContact: boolean,
    hasProfileImage: boolean,
    useSeparator: boolean,
    numItems: number,
    hasBackgroundImage: boolean,
    hasBio: boolean
) => {
    let computedHeight = BASE_HEIGHT;

    if (embedMode === EmbedMode.normal) {
        if (hasProfileImage) computedHeight += EMBED_PROFILE_BLOCKS[EmbedMode.normal].PROFILE_PHOTO;
        computedHeight += EMBED_PROFILE_BLOCKS[EmbedMode.normal].TITLE;
        if (hasBio) computedHeight += EMBED_PROFILE_BLOCKS[EmbedMode.normal].BIO;

        if (hasSocialsOrContact) computedHeight += EMBED_PROFILE_BLOCKS[EmbedMode.normal].SOCIALS_CONTACT;
    }

    if (embedMode === EmbedMode.compact) {
        if (hasSocialsOrContact) computedHeight += EMBED_PROFILE_BLOCKS[EmbedMode.compact].WITH_SOCIALS;
        else if (hasProfileImage) computedHeight += EMBED_PROFILE_BLOCKS[EmbedMode.compact].WITH_IMAGE;
        else computedHeight += EMBED_PROFILE_BLOCKS[EmbedMode.compact].WITHOUT_SOCIALS_AND_IMAGE;
    }

    if (useSeparator) computedHeight += EMBED_PROFILE_BLOCKS.SEPARATOR;

    if (hasBackgroundImage && numItems > 0) computedHeight += 40;

    computedHeight += getListHeight(numItems);

    computedHeight += EMBED_COLLECTION_BLOCKS.VIEW_ON_AUTHORY;

    if (hasBackgroundImage && numItems === 0) computedHeight += EMBED_PROFILE_BLOCKS.BACKGROUND_NO_ITEMS_FOOTER;

    return computedHeight;
};
